import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Flame, LogOut, Phone, Mail, RefreshCw, Lock, MapPin } from "lucide-react";
import { Input } from "./ui/input";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_COLOR = {
  caldaia: "bg-[#FF6B00]/15 text-[#FF6B00] border-[#FF6B00]/30",
  condizionatore: "bg-[#00D2FF]/15 text-[#00D2FF] border-[#00D2FF]/30",
  altro: "bg-white/10 text-[#94A3B8] border-white/20",
};

export const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLeads = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setLeads(data);
      setError("");
    } catch {
      setToken("");
      localStorage.removeItem("admin_token");
      setError("Sessione scaduta. Accedi di nuovo.");
      setLeads(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const doLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#07090D] text-[#F1F5F9] flex items-center justify-center px-6">
        <form
          onSubmit={doLogin}
          data-testid="admin-login-form"
          className="glass rounded-3xl p-8 md:p-10 w-full max-w-sm flex flex-col gap-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <LogoMark className="w-10 h-10" />
            <div>
              <p className="font-display font-semibold text-lg leading-tight">F&C Impianti</p>
              <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                Area riservata
              </p>
            </div>
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              type="password"
              data-testid="admin-password-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 pl-11 rounded-xl bg-white/[0.04] border-white/10 text-[#F1F5F9] placeholder:text-[#94A3B8]/60 focus-visible:ring-[#FF6B00]"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="rounded-full bg-[#FF6B00] text-[#07090D] font-semibold px-8 py-3.5 text-sm uppercase tracking-wider transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,107,0,0.4)] active:scale-[0.98] disabled:opacity-60"
          >
            {loading ? "Accesso..." : "Accedi"}
          </button>
          <a href="/" className="text-center text-xs text-[#94A3B8] hover:text-white transition-colors">
            ← Torna al sito
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07090D] text-[#F1F5F9]">
      <header className="glass sticky top-0 z-40">
        <div className="mx-auto max-w-4xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="w-8 h-8" />
            <span className="font-display font-semibold">Richieste ricevute</span>
            {leads && (
              <span data-testid="admin-leads-count" className="font-mono-tech text-xs bg-[#FF6B00]/15 text-[#FF6B00] rounded-full px-3 py-1">
                {leads.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchLeads(token)}
              data-testid="admin-refresh-button"
              className="p-2.5 rounded-full border border-white/10 hover:border-[#00D2FF] transition-colors"
              aria-label="Aggiorna"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => { localStorage.removeItem("admin_token"); setToken(""); setLeads(null); }}
              data-testid="admin-logout-button"
              className="p-2.5 rounded-full border border-white/10 hover:border-red-400 hover:text-red-400 transition-colors"
              aria-label="Esci"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10" data-testid="admin-leads-list">
        {leads === null ? (
          <p className="text-center text-[#94A3B8] py-20">Caricamento...</p>
        ) : leads.length === 0 ? (
          <div className="text-center py-20">
            <Flame className="w-10 h-10 text-[#FF6B00] mx-auto mb-4" />
            <p className="text-[#94A3B8]">Nessuna richiesta ancora. Le nuove richieste del modulo appariranno qui.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {leads.map((lead) => (
              <article
                key={lead.id}
                data-testid={`lead-item-${lead.id}`}
                className="rounded-2xl border border-white/10 bg-[#12151C] p-5 md:p-6"
              >
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h3 className="font-display font-semibold text-lg">{lead.nome}</h3>
                  <span className={`font-mono-tech text-[11px] uppercase tracking-wider rounded-full border px-3 py-1 ${TIPO_COLOR[lead.tipo]}`}>
                    {lead.tipo}
                  </span>
                  <time className="ml-auto text-xs text-[#94A3B8]">
                    {new Date(lead.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
                  </time>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm mb-3">
                  <a href={`tel:${lead.telefono}`} className="flex items-center gap-2 text-[#FF6B00] font-semibold">
                    <Phone className="w-4 h-4" /> {lead.telefono}
                  </a>
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-[#00D2FF]">
                      <Mail className="w-4 h-4" /> {lead.email}
                    </a>
                  )}
                  {lead.via && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lead.via)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors"
                    >
                      <MapPin className="w-4 h-4 text-[#FF6B00]" /> {lead.via}
                    </a>
                  )}
                </div>
                {lead.messaggio && (
                  <p className="text-sm text-[#94A3B8] leading-relaxed border-t border-white/5 pt-3">
                    {lead.messaggio}
                  </p>
                )}
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
